import React, { useMemo } from 'react';
import clsx from 'clsx';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import {
  MOTION_SIGNATURES,
  motionArtworkLoop,
  motionArtworkPose,
  motionGroundPose,
  type MotionArtworkPose,
} from 'fym-shared/src/music24/story/motionArtwork';
import { TypedMemo } from '../../../../common/types';
import { useLoopFrame } from './useLoopFrame';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the frames; each frame uses its own slice of it */
import styles from './AssetFrame.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface MotionArtworkFrameProps {
  themeId: ReleaseThemeId;
  vars: React.CSSProperties;
  /** The frames take the height of the box (the step-2 card) instead of its width. */
  fixedHeight?: boolean;
  /** False: frame 0 only (the step-2 card, reduced motion). */
  playing?: boolean;
  className?: string;
}

/** The preview loop: the server fits whole bars of the song; the browser has no beat grid, so 20 s in 10 phrases. */
const PREVIEW_LOOP = motionArtworkLoop(null);

/** The class of each light (`MOTION_SIGNATURES[theme].light`). */
const LIGHT_CLASS: Record<string, string | undefined> = {
  bloom: styles.maLightBloom,
  glow: styles.maLightGlow,
  sun: styles.maLightSun,
  sweep: styles.maLightSweep,
};

/** The light layer of a pose as CSS (screen blend over the cover). */
function lightStyle(themeId: ReleaseThemeId, pose: MotionArtworkPose): React.CSSProperties | null {
  const { light } = MOTION_SIGNATURES[themeId];
  if (light === 'none' || pose.light <= 0) {
    return null;
  }
  if (light === 'sweep') {
    const at = Math.round((pose.sweepAt ?? 0) * 1000) / 10;
    return {
      opacity: pose.light,
      background: `linear-gradient(105deg, transparent ${at - 12}%, #ffffff ${at}%, transparent ${at + 12}%)`,
    };
  }
  return { opacity: pose.light };
}

/**
 * The motion artwork pair as it plays (design `asset-redesign/w`, `MotionFrame`): the square file
 * (the cover fills it) and the tall file (the whole cover in the iPhone safe band over its blurred
 * copy). The motion is the signature of the release theme from the shared table
 * (`motionArtworkPose`, the same curves the ffmpeg renderer draws); frame 0 is the cover. No text,
 * no frame.
 */
const MotionArtworkFrameComponent: React.FC<MotionArtworkFrameProps> = ({
  themeId,
  vars,
  fixedHeight,
  playing = true,
  className,
}) => {
  const frame = useLoopFrame(PREVIEW_LOOP.loopFrames, playing);
  const pose = useMemo(() => motionArtworkPose(themeId, frame, PREVIEW_LOOP), [frame, themeId]);
  const ground = useMemo(() => motionGroundPose(frame, PREVIEW_LOOP.loopFrames), [frame]);
  const { light } = MOTION_SIGNATURES[themeId];
  const lightCss = lightStyle(themeId, pose);
  const transform = `scale(${pose.scale}) rotate(${pose.turnDeg}deg)`;
  const lightLayer = lightCss !== null && <i className={clsx(styles.maLight, LIGHT_CLASS[light])} style={lightCss} />;
  return (
    <div
      className={clsx(styles.pair, fixedHeight === true && styles.pairFixed, className)}
      style={vars}
      aria-hidden="true"
      data-frame="motionArtwork"
      data-theme={themeId}
    >
      <div className={clsx(styles.fv, styles.fv11)} data-frame="motionSquare">
        <i className={styles.full} style={{ transform }} />
        {lightLayer}
      </div>
      <div className={clsx(styles.fv, styles.fv34)} data-frame="motionTall">
        <i
          className={styles.maGround}
          style={{
            transform: `translate(${ground.dx * 100}%, ${ground.dy * 100}%) scale(${1.06 * ground.scale})`,
          }}
        />
        <i className={styles.maCover} style={{ transform }} />
        {lightLayer}
      </div>
    </div>
  );
};

export const MotionArtworkFrame = TypedMemo(MotionArtworkFrameComponent);
